/**
 * RightFacePanel — face enhancement controls, skin tone picker, CodeFormer UI.
 */

import { useState } from "react";
import { EffectCard, EffectSlider } from "../shell/Panel";
import { API_BASE } from "@/lib/api";

type EnhancerType = "none" | "gfpgan" | "codeformer";

export interface RightFacePanelProps {
  enhanceFace: boolean;
  gfpganQuality: "fast" | "high";
  enhancerType: EnhancerType;
  codeformerFidelity: number;
  autoEnhancer: string;
  eyedropperActive: boolean;
  activeProfile: { profile_id: string; name: string; thumbnail_url?: string; dataUrl?: string } | undefined;
  hardware: any;
  onEnhanceFaceToggle: () => void;
  onGfpganQualityChange: (q: "fast" | "high") => void;
  onEnhancerChange: (e: EnhancerType) => void;
  onFidelityChange: (v: number) => void;
  onEyedropperToggle: () => void;
  onSkinTonePick: (x: number, y: number) => void;
}

export function RightFacePanel({
  enhanceFace,
  gfpganQuality, enhancerType, codeformerFidelity, autoEnhancer,
  eyedropperActive, activeProfile, hardware,
  onEnhanceFaceToggle,
  onGfpganQualityChange, onEnhancerChange, onFidelityChange,
  onEyedropperToggle, onSkinTonePick,
}: RightFacePanelProps) {

  const handleImgClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!eyedropperActive || !activeProfile) return;
    const img = e.currentTarget;
    const rect = img.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    onSkinTonePick(x, y);
  };

  const ENHANCERS: { id: EnhancerType; label: string }[] = [
    { id: "none", label: "None" },
    { id: "gfpgan", label: "GFPGAN" },
    { id: "codeformer", label: "CodeFormer" },
  ];

  return (
    <div style={{ paddingTop: "var(--space-3)" }}>

      {/* Face Restore */}
      <EffectCard title="Face Restore" enabled={enhanceFace} onToggle={onEnhanceFaceToggle}>
        {/* Auto-detected badge */}
        <div style={{
          display: "flex", alignItems: "center", gap: "var(--space-2)",
          marginBottom: "var(--space-3)",
        }}>
          <span style={{
            fontSize: "var(--text-xs)", padding: "2px 8px",
            borderRadius: "var(--radius-full)",
            background: "var(--status-proc-dim)",
            color: "var(--status-proc)",
            fontWeight: "var(--weight-semibold)",
            border: "1px solid var(--status-proc)",
          }}>
            Auto: {autoEnhancer === "codeformer" ? "CodeFormer" : autoEnhancer === "gfpgan" ? "GFPGAN" : "None"}
          </span>
        </div>

        {/* Enhancer selector pills */}
        <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
          {ENHANCERS.map(e => (
            <button
              key={e.id}
              onClick={() => onEnhancerChange(e.id)}
              style={{
                flex: 1, height: 28,
                borderRadius: "var(--radius-md)",
                border: `1px solid ${enhancerType === e.id ? "var(--accent)" : "var(--border-default)"}`,
                background: enhancerType === e.id ? "var(--accent-dim)" : "var(--bg-floating)",
                color: enhancerType === e.id ? "var(--accent)" : "var(--text-secondary)",
                fontSize: "var(--text-xs)",
                fontWeight: enhancerType === e.id ? "var(--weight-semibold)" : "var(--weight-medium)",
                cursor: "pointer",
                fontFamily: "var(--font-sans)",
                transition: "all var(--duration-fast)",
              }}
            >
              {e.label}
            </button>
          ))}
        </div>

        {/* GFPGAN quality selector */}
        {enhancerType === "gfpgan" && (
          <div style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
            {(["fast", "high"] as const).map(q => (
              <button
                key={q}
                onClick={() => onGfpganQualityChange(q)}
                style={{
                  flex: 1, height: 28,
                  borderRadius: "var(--radius-md)",
                  border: `1px solid ${gfpganQuality === q ? "var(--accent)" : "var(--border-default)"}`,
                  background: gfpganQuality === q ? "var(--accent-dim)" : "var(--bg-floating)",
                  color: gfpganQuality === q ? "var(--accent)" : "var(--text-secondary)",
                  fontSize: "var(--text-xs)",
                  fontWeight: gfpganQuality === q ? "var(--weight-semibold)" : "var(--weight-medium)",
                  cursor: "pointer",
                  fontFamily: "var(--font-sans)",
                  transition: "all var(--duration-fast)",
                }}
              >
                {q === "fast" ? "Fast (~300ms)" : "High (~2s)"}
              </button>
            ))}
          </div>
        )}

        {/* CodeFormer fidelity slider */}
        {enhancerType === "codeformer" && (
          <EffectSlider
            label="Identity ← → Quality"
            value={Math.round(codeformerFidelity * 100)}
            min={0} max={100}
            onChange={v => onFidelityChange(v / 100)}
          />
        )}
      </EffectCard>



      {/* Source photo + eyedropper */}
      {activeProfile && (
        <div style={{
          margin: "var(--space-3)",
          padding: "var(--space-3)",
          borderRadius: "var(--radius-lg)",
          background: "var(--bg-overlay)",
          border: "1px solid var(--border-subtle)",
        }}>
          <div style={{
            fontSize: "var(--text-xs)", color: "var(--text-muted)",
            fontWeight: "var(--weight-semibold)", textTransform: "uppercase" as const,
            letterSpacing: "0.06em", marginBottom: "var(--space-3)",
          }}>
            Source Photo
          </div>
          <div style={{ display: "flex", gap: "var(--space-3)", alignItems: "flex-start" }}>
            <div style={{ position: "relative", flexShrink: 0, width: 80, height: 80, borderRadius: "var(--radius-lg)", overflow: "hidden", background: "var(--bg-elevated)", border: `2px solid ${eyedropperActive ? "var(--status-warn)" : "var(--border-subtle)"}` }}>
              {(activeProfile.thumbnail_url || (activeProfile as any).dataUrl) ? (
                <img
                  src={activeProfile.thumbnail_url || (activeProfile as any).dataUrl}
                  alt={activeProfile.name}
                  onClick={handleImgClick}
                  style={{
                    width: "100%", height: "100%",
                    objectFit: "cover",
                    cursor: eyedropperActive ? "crosshair" : "default",
                    display: "block",
                  }}
                />
              ) : (
                <div 
                  onClick={handleImgClick}
                  style={{
                    width: "100%", height: "100%",
                    display: "flex", flexDirection: "column",
                    alignItems: "center", justifyContent: "center",
                    background: "linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.04))",
                    color: "var(--text-secondary)",
                    cursor: eyedropperActive ? "crosshair" : "default",
                  }}
                >
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              )}
              {eyedropperActive && (
                <div style={{
                  position: "absolute", inset: 0,
                  borderRadius: "var(--radius-lg)",
                  background: "rgba(245,158,11,0.15)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  pointerEvents: "none",
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--status-warn)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 19l7-7 3 3-7 7-3-3z"/>
                    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18"/>
                    <path d="M2 2l7.586 7.586"/>
                    <circle cx="11" cy="11" r="2"/>
                  </svg>
                </div>
              )}
            </div>
            <div style={{ flex: 1, display: "flex", flexDirection: "column" as const, gap: "var(--space-2)" }}>
              <button
                onClick={onEyedropperToggle}
                style={{
                  height: 28, padding: "0 var(--space-3)",
                  borderRadius: "var(--radius-md)",
                  border: `1px solid ${eyedropperActive ? "var(--status-warn)" : "var(--border-default)"}`,
                  background: eyedropperActive ? "var(--status-warn-dim)" : "var(--bg-floating)",
                  color: eyedropperActive ? "var(--status-warn)" : "var(--text-secondary)",
                  fontSize: "var(--text-xs)", fontWeight: "var(--weight-semibold)",
                  cursor: "pointer", fontFamily: "var(--font-sans)",
                  transition: "all var(--duration-fast)",
                  width: "100%",
                }}
              >
                {eyedropperActive ? "× Cancel" : "Pick Skin Tone"}
              </button>
              <div style={{ fontSize: "var(--text-xs)", color: "var(--text-muted)", lineHeight: 1.4 }}>
                {eyedropperActive
                  ? "Click on the face area to set skin tone"
                  : "Click to pick exact skin tone from photo"}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
